/**
 * Brand self-service domain-join (Phase 3).
 *
 * Settings management (owner-only):
 *   getDomainAccessSettings, putDomainAccessSettings
 *
 * Join-request flow:
 *   requestJoin, verifyJoin (public) / listJoinRequests, approveJoinRequest,
 *   rejectJoinRequest (owner + brand_admin) / verifyJoinAccept, acceptJoin (public)
 */
import { apiClient } from '@discovr/common';
import type { DataResponse, PaginatedResponse } from '@discovr/common';
import type { PendingGrant } from './members';

export interface DomainJoinSettings {
  enabled: boolean;
  domains?: string[];
}

/** The role an owner/brand_admin may grant when inviting or approving a join
 * request — never brand_owner, which is never invite/join-reachable. */
export type BrandApprovalRole = 'brand_member' | 'brand_admin';

export interface BrandJoinRequest {
  id: string;
  brand_id: string;
  email: string;
  status: 'pending' | 'approved' | 'rejected' | 'accepted';
  created_at: string;
  updated_at: string;
}

export interface VerifyJoinRequestResponse {
  status: string;
  brand_name: string;
}

export interface BrandLookupResult {
  slug: string;
  name: string;
}

export interface VerifyJoinAcceptResponse {
  email: string;
  brand_name: string;
  [key: string]: unknown;
}

export interface AcceptJoinResponse {
  firebase_custom_token: string;
  user: unknown;
}

export const domainJoin = {
  // ─── Settings (authenticated, owner-only) ────────────────────────────────

  /** Get the caller's brand's domain-join settings. */
  getDomainAccessSettings: (): Promise<DomainJoinSettings> =>
    apiClient
      .get<DataResponse<DomainJoinSettings>>('/brands/me/settings/domain-join')
      .then((r) => r.data.data),

  /** Update the caller's brand's domain-join settings (owner-only). */
  putDomainAccessSettings: (body: { enabled: boolean; domains: string[] }): Promise<DomainJoinSettings> =>
    apiClient
      .put<DataResponse<DomainJoinSettings>>('/brands/me/settings/domain-join', body)
      .then((r) => r.data.data),

  // ─── Public (no auth) ─────────────────────────────────────────────────────

  /**
   * Look up which brand(s) an email can request to join, by domain — the
   * entry point when the caller doesn't already know a brand's slug (unlike
   * requestJoin/verifyJoin below, which require one). Read-only: does not
   * send an OTP or create anything. Always resolves; an empty array covers
   * every non-match reason (brand doesn't exist, domain-join disabled, or
   * the domain isn't allow-listed) — enumeration-safe by construction.
   */
  lookupBrands: (email: string): Promise<BrandLookupResult[]> =>
    apiClient
      .post<DataResponse<{ brands: BrandLookupResult[] }>>('/join-requests/brand/lookup', { email })
      .then((r) => r.data.data.brands),

  /** Start a join request — sends an OTP if eligible (public, enumeration-safe). */
  requestJoin: (body: { brand: string; email: string }): Promise<void> =>
    apiClient.post('/join-requests/brand/start', body).then(() => undefined),

  /** Verify the OTP and create the pending join request (public). */
  verifyJoin: (body: { brand: string; email: string; code: string }): Promise<VerifyJoinRequestResponse> =>
    apiClient
      .post<DataResponse<VerifyJoinRequestResponse>>('/join-requests/brand/verify', body)
      .then((r) => r.data.data),

  /** Verify a join-request accept token (public, from the approval email link). */
  verifyJoinAccept: (token: string): Promise<VerifyJoinAcceptResponse> => {
    const params = new URLSearchParams({ token });
    return apiClient
      .get<DataResponse<VerifyJoinAcceptResponse>>(`/join-requests/accept/verify?${params}`)
      .then((r) => r.data.data);
  },

  /** Accept a join request and set a password (public, from the approval email link). */
  acceptJoin: (body: {
    token: string;
    password: string;
    first_name: string;
    last_name: string;
  }): Promise<AcceptJoinResponse> =>
    apiClient
      .post<DataResponse<AcceptJoinResponse>>('/join-requests/accept', body)
      .then((r) => r.data.data),

  /** Accept a join request via a Google sign-in the FE already completed client-side (public). */
  acceptJoinGoogle: (body: { token: string; id_token: string }): Promise<AcceptJoinResponse> =>
    apiClient
      .post<DataResponse<AcceptJoinResponse>>('/join-requests/accept-google', body)
      .then((r) => r.data.data),

  // ─── Authenticated (owner + brand_admin) ───────────────────────────────────

  /** List the caller's brand's pending join requests. */
  listJoinRequests: (params?: { page?: number; limit?: number }): Promise<PaginatedResponse<BrandJoinRequest>> =>
    apiClient
      .get<PaginatedResponse<BrandJoinRequest>>('/brands/me/join-requests', { params })
      .then((r) => r.data),

  /** Approve a pending join request, optionally granting campaign access up front. */
  approveJoinRequest: (
    id: string,
    role: BrandApprovalRole,
    campaign_grants?: PendingGrant[],
  ): Promise<{ status: string; accept_url: string }> =>
    apiClient
      .post<DataResponse<{ status: string; accept_url: string }>>(
        `/brands/me/join-requests/${encodeURIComponent(id)}/approve`,
        { role, campaign_grants },
      )
      .then((r) => r.data.data),

  /** Reject a pending join request. WriteNoContent → 204. */
  rejectJoinRequest: (id: string): Promise<void> =>
    apiClient.post(`/brands/me/join-requests/${encodeURIComponent(id)}/reject`).then(() => undefined),

  /** Re-send the accept link for an approved-but-not-yet-onboarded join request. WriteNoContent → 204. */
  resendJoinRequest: (id: string): Promise<void> =>
    apiClient.post(`/brands/me/join-requests/${encodeURIComponent(id)}/resend`).then(() => undefined),
};
